// Live relay state for a switch-type device, shared by both UIs.
//
// Two things matter here, and both are lifted straight from the Flutter screens:
//
// 1. UNKNOWN IS NOT OFF. Until a real status arrives (server seed or MQTT), each
//    relay is "unknown" and the UI paints it BLUE. Flutter learned this the hard
//    way: defaulting to OFF made a running load look switched off whenever the
//    seed fetch was slow.
// 2. The UI never guesses. Tapping a relay publishes a command and nothing else;
//    the colour changes only when the DEVICE echoes its status back. So what you
//    see is always what the hardware reported.
//
// Status wire format on `<uno>/client` is `[<uno>]<status>#<userId>`, and for
// switch devices <status> is a CSV of 0/1 — one per relay.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  subscribeTopic, unsubscribeTopic, addMessageListener,
} from '../mqtt/mqttClient';
import { fetchSeedStatus } from '../devices/deviceStatus';
import { publishCommand } from '../utils/deviceController';

// Parse a CSV status into { states, known, values } for `count` relays.
//
// ⚠️ PER FIELD, NOT ALL-OR-NOTHING — and this was a real fault, not a tidy-up.
// The check used to be `parts.every(p => p === '0' || p === '1')`, rejecting the
// WHOLE frame if any field was not a bare boolean. A panel with a fan ends in a
// SPEED (10 for off, then 30…100), so a 4+1 seeding `0,0,0,0,10` failed that
// test and every tile stayed "unknown" blue for ever — the four switches did not
// work either, on a device whose only unusual field was the fifth one.
//
// The live listener below already reads field by field; this now matches it. A
// field that is not 0/1 simply leaves that index unknown, which is the same
// conservative answer as before for genuinely malformed input.
//
// `values` are the raw numbers, so a fan channel's speed survives — booleans
// cannot express one, and `40` read as a boolean is just "on".
function parseCsv(raw, count) {
  const single = parseSingle(raw);
  if (single) {
    return {
      states: Array.from({ length: count }, (_, i) => i === single.index && single.on),
      known: Array.from({ length: count }, (_, i) => i === single.index),
      values: [],
    };
  }
  const parts = String(raw ?? '').trim().split(',').map((s) => s.trim());
  return {
    states: Array.from({ length: count }, (_, i) => parts[i] === '1'),
    known: Array.from({ length: count }, (_, i) => parts[i] === '0' || parts[i] === '1'),
    values: parts.map((p) => (p === '' || Number.isNaN(Number(p)) ? null : Number(p))),
  };
}

// ⚠️ SOME DEVICES REPORT ONE RELAY, NOT A CSV — `1-1` / `1-0`, the same
// `<relay>-<0|1>` words the app sends. The 1-gang AC switch (DeviceID 60) does
// it, and the server stores it as the seed: `[1073599]1-1`. Read as a CSV, the
// one field `1-1` is neither 0 nor 1, so the tile stayed "unknown" BLUE for ever
// on a device that was online and ON — which looked like a BLE marker.
// Only 0/1 counts: `1-100` is a fan speed and is left to the CSV path.
function parseSingle(raw) {
  const m = /^(\d+)-([01])$/.exec(String(raw ?? '').trim());
  if (!m || Number(m[1]) < 1) return null;
  return { index: Number(m[1]) - 1, on: m[2] === '1' };
}

export function useRelayStatus(uniqueNumber, count, userId) {
  const [states, setStates] = useState(() => new Array(count).fill(false));
  const [known, setKnown] = useState(() => new Array(count).fill(false));
  // Every field of the last frame, raw. The switch tiles use `states`; a fan
  // channel needs the number, because its value is a speed and not a boolean.
  const [values, setValues] = useState([]);
  const countRef = useRef(count);
  countRef.current = count;

  // Seed from the last status the server recorded for this device.
  useEffect(() => {
    if (!uniqueNumber) return undefined;
    let alive = true;
    fetchSeedStatus(uniqueNumber).then((raw) => {
      if (!alive) return;
      const parsed = parseCsv(raw, countRef.current);
      setStates(parsed.states);
      setKnown(parsed.known);
      setValues(parsed.values);
    });
    return () => { alive = false; };
  }, [uniqueNumber]);

  // Then follow live status.
  useEffect(() => {
    if (!uniqueNumber) return undefined;
    const topic = `${uniqueNumber}/client`;
    subscribeTopic(topic);
    const off = addMessageListener((t, payload) => {
      if (t !== topic) return;
      // Guard against other devices' frames arriving on a shared listener.
      const open = payload.indexOf('[');
      const close = payload.indexOf(']');
      if (open === -1 || close === -1) return;
      if (payload.substring(open + 1, close) !== String(uniqueNumber)) return;

      const s = `${payload}#`;
      const body = s.substring(s.indexOf(']') + 1, s.indexOf('#'));
      const single = parseSingle(body);
      if (single) {
        setStates((prev) => prev.map((v, i) => (i === single.index ? single.on : v)));
        setKnown((prev) => prev.map((v, i) => (i === single.index ? true : v)));
        return;
      }
      const bits = body.split(',').map((x) => x.trim());
      setStates((prev) => prev.map((v, i) => (bits[i] === '1' ? true : bits[i] === '0' ? false : v)));
      setKnown((prev) => prev.map((v, i) => (bits[i] === '0' || bits[i] === '1' ? true : v)));
      setValues(bits.map((p) => (p === '' || Number.isNaN(Number(p)) ? null : Number(p))));
    });
    return () => { off(); unsubscribeTopic(topic); };
  }, [uniqueNumber]);

  // Resize when the relay count is known later (e.g. after outputs load).
  useEffect(() => {
    setStates((prev) => (prev.length === count ? prev
      : Array.from({ length: count }, (_, i) => prev[i] ?? false)));
    setKnown((prev) => (prev.length === count ? prev
      : Array.from({ length: count }, (_, i) => prev[i] ?? false)));
  }, [count]);

  // Send "<relay>-<0|1>" for a 1-based relay index. No optimistic update.
  const toggleRelay = useCallback((index) => {
    const turnOn = !states[index];
    publishCommand(uniqueNumber, `${index + 1}-${turnOn ? '1' : '0'}`, userId);
  }, [states, uniqueNumber, userId]);

  const setAll = useCallback((on) => {
    publishCommand(uniqueNumber, on ? 'allon' : 'alloff', userId);
  }, [uniqueNumber, userId]);

  // A fan channel is 1-based like a relay and takes a SPEED, not 0/1 — the
  // ladder is 10 (off) then 30…100. Same envelope as every other command, so
  // the ESP8266 fleet understands it; no Tuya DP is involved.
  const setChannel = useCallback((index, value) => {
    publishCommand(uniqueNumber, `${index + 1}-${value}`, userId);
  }, [uniqueNumber, userId]);

  return {
    states, known, values, toggleRelay, setAll, setChannel,
  };
}
